// "Metti in sospeso" and "Rilascia sospensione" on the PRA case (Stage 2 slice 8). Plain module with
// no Vue, so it runs under `node --test`. Labels are i18n keys; the API checks everything again and
// keeps the state unchanged while on hold. Any User, Manager or Admin places and releases a hold.

import { checkUpload } from "./dossierFiles.mjs";

// Same list and limits as the API (constants PRA_HOLD_TYPES, provisional; pra/holds.js note;
// Stage 1 parseReason).
export const HOLD_TYPES = [
  "tax_hold",
  "judicial_seizure",
  "abandoned_vehicle",
  "other",
];
export const HOLD_NOTE_MAX = 1000;
export const RELEASE_REASON_MAX = 500;

// The step the case can record again after the release, by state (a confirmed case has none).
const NEXT_STEP = {
  open: "crd",
  crd_recorded: "request",
  deregistration_requested: "receipt",
};

export function emptyHoldForm() {
  return { type: "", note: "", file: null };
}

// { field: reason } for what blocks the save; {} when it can be sent. "required" only disables the
// button; the other reasons show under the field. The file is optional.
export function holdFormErrors(form) {
  const errors = {};
  const type = (form && form.type) || "";
  if (!type) errors.type = "required";
  else if (!HOLD_TYPES.includes(type)) errors.type = "invalid";
  const note = String((form && form.note) || "").trim();
  if (!note) errors.note = "required";
  else if (note.length > HOLD_NOTE_MAX) errors.note = "too_long";
  if (form && form.file) {
    const problem = checkUpload(form.file, "pra");
    if (problem) errors.file = problem;
  }
  return errors;
}

// One commandId per hold: a retry of the same input reuses it, so a double click places it once.
export function holdSubmitKey(form) {
  const file = form.file || {};
  return [
    form.type,
    String(form.note).trim(),
    file.name,
    file.size,
    file.lastModified,
  ].join("|");
}

// What blocks "Rilascia": { reason } with "required" or "too_long"; {} when it can be sent.
export function releaseFormErrors({ reason } = {}) {
  const text = String(reason || "").trim();
  if (!text) return { reason: "required" };
  if (text.length > RELEASE_REASON_MAX) return { reason: "too_long" };
  return {};
}

// A retry of the same release reuses its commandId.
export function releaseSubmitKey(holdId, reason) {
  return [holdId, String(reason || "").trim()].join("|");
}

// The "Metti in sospeso" button: shown while the case is open and not on hold. A held case shows
// "Rilascia sospensione" in its banner instead; a confirmed case has neither.
export function holdAction(view) {
  const praCase = view && view.praCase;
  if (!praCase || praCase.state === "confirmed" || praCase.activeHold)
    return null;
  return { disabled: false };
}

// The release note "La pratica torna a «{state}» e si può registrare {step}.": the i18n keys of the
// state and of the step that becomes possible again, or null for a state without a next step.
export function releaseNote(praCase) {
  const state = praCase && praCase.state;
  const step = NEXT_STEP[state];
  if (!step) return null;
  return {
    stateKey: `acceptance.pra.states.${state}`,
    stepKey: `acceptance.pra.hold.next_steps.${step}`,
  };
}

// The released holds, newest first ("Sospensioni precedenti").
export function pastHolds(praCase) {
  return ((praCase && praCase.holds) || []).slice().reverse();
}

// The i18n key of a hold type; an unknown type shows as it is.
export function holdTypeKey(type) {
  return HOLD_TYPES.includes(type) ? `acceptance.pra.hold.types.${type}` : null;
}

// A failed hold or release -> the i18n key of its message, or null to show the API's errorMsg.
// Messages that read the same as elsewhere reuse those keys.
const HOLD_ERRORS = {
  already_on_hold: "acceptance.pra.hold.errors.already_on_hold",
  not_on_hold: "acceptance.pra.hold.errors.not_on_hold",
  already_confirmed: "acceptance.pra.crd.errors.already_confirmed",
  unsupported_type: "acceptance.pra.crd.errors.unsupported_type",
  reason_required: "acceptance.pra.file_change.errors.reason_required",
};

export function holdErrorKey(err) {
  const res = err && err.response;
  if (!res) return null;
  // A proxy in front of the API may answer 413 without our JSON body.
  if (res.status === 413) return "acceptance.pra.crd.errors.too_large";
  const code = res.data && res.data.code;
  return HOLD_ERRORS[code] || null;
}
